<template>
    <div class="account">
        <div class="account__title h5" v-translate>about_you</div>
        <div class="account__text text" v-translate>account_intro</div>

        <!-- profile image -->
        <div class="account__profile-image">
            <div class="account__profile-image__image">
                <v-lazy-image :src="imagePreviewUrl ? imagePreviewUrl : user.profileImageUrl" v-if="imagePreviewUrl || user.profileImageUrl"></v-lazy-image>
                <transition name="fade">
                    <Loader class="account__profile-image__loader" color="white" v-if="imageUploading"></Loader>
                </transition>
            </div>
            <div class="account__profile-image__label text">
                <span v-if="imagePreviewUrl || user.profileImageUrl" v-translate>change_profile_picture</span>
                <span v-else v-translate>add_profile_picture</span>
            </div>
            <input type="file" class="account__profile-image__input" accept="image/*" @change="onImageFileChange">
        </div>

        <div class="account__form form">
            <!-- name, email, location -->
            <div class="form__row form__row--multiple">
                <div class="form__item form__item--half">
                    <input @input="requiredFieldChange" type="text" class="form__field form__field--text" :placeholder="placeholders.name  + '*'" v-model="formData.name" :class="{'is-error': errors.indexOf('name') >= 0}">
                    <div class="form__error" v-if="errors.indexOf('name') >= 0">"{{placeholders.name}}" <translate>form_field_required</translate></div>
                </div>
                <div class="form__item form__item--half">
                    <input @input="requiredFieldChange" type="email" class="form__field form__field--text" :placeholder="placeholders.email  + '*'" v-model="formData.email" :class="{'is-error': errors.indexOf('email') >= 0}">
                    <div class="form__error" v-if="errors.indexOf('email') >= 0">"{{placeholders.email}}" <translate>form_field_required</translate></div>
                </div>
            </div>
            <div class="form__row form__row--multiple form__row--space-below">
                <div class="form__item form__item--half">
                    <!-- <input type="text" class="form__field form__field--text" :placeholder="placeholders.location" v-model="formData.location"> -->
                    <v-select 
                        class="form__v-select" 
                        :class="{'is-error': errors.indexOf('location') >= 0}"
                        @input="requiredFieldChange"
                        v-model="formData.location" 
                        :reduce="item => item.label"
                        :options="countryOptions[0]" 
                        :placeholder="placeholders.location + '*'"
                    >
                    </v-select>
                    <div class="form__error" v-if="errors.indexOf('location') >= 0">"{{placeholders.location}}" <translate>form_field_required</translate></div>
                </div>
                <div class="form__item form__item--half">
                </div>
            </div>

            <!-- gender, dob, height, weight -->
            <div class="form__row form__row--multiple">
                <!-- <div class="form__item form__item--half">
                    <v-select 
                        class="form__v-select" 
                        v-model="formData.sex"
                        @input="requiredFieldChange"
                        :reduce="item => item.code"
                        :options="genderOptions" 
                        :placeholder="placeholders.gender + '*'"
                        :class="{'is-error': errors.indexOf('sex') >= 0}"
                        :clearable="false"
                    ></v-select>
                    <div class="form__error" v-if="errors.indexOf('sex') >= 0">"{{placeholders.gender}}" <translate>form_field_required</translate></div>
                </div> -->
                <div class="form__item form__item--half">
                    <v-select 
                        class="form__v-select" 
                        v-model="formData.sex"
                        :reduce="item => item.code"
                        :options="genderOptions" 
                        :placeholder="placeholders.gender"
                        :clearable="true"
                    ></v-select>
                </div>
                <div class="form__item form__item--date form__item--half">
                     <svg class="icon form__icon"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                        >
                            <g>
                                <path class="st0" d="M19,4H5C4.4,4,4,4.4,4,5v14c0,0.6,0.4,1,1,1h14c0.6,0,1-0.4,1-1V5C20,4.4,19.6,4,19,4z M19,19H5V8.5h14V19z
                                    M19,7.5H5V5h14V7.5z"/>
                                <rect x="7" y="10.5" class="st0" width="2" height="2"/>
                                <rect x="11" y="10.5" class="st0" width="2" height="2"/>
                                <rect x="15" y="10.5" class="st0" width="2" height="2"/>
                                <rect x="7" y="14.5" class="st0" width="2" height="2"/>
                                <rect x="11" y="14.5" class="st0" width="2" height="2"/>
                                <rect x="15" y="14.5" class="st0" width="2" height="2"/>
                            </g>
                        </svg>

                        <datepicker
                            @input="handleDateChange"
                            :value="formData.dob"
                            format="yyyy-MM-dd"
                            input-class="form__field" 
                            :placeholder="placeholders.birthDate" 
                            :disabled-dates="disabledDates"
                            :open-date="initialDate"
                            initial-view="year"
                            :use-utc="true"
                        ></datepicker>
                </div>
            </div>
            <div class="form__row form__row--multiple form__row--space-below">
                <div class="form__item form__item--half">
                    <input  type="number" 
                            :min="minHeight" 
                            :max="maxHeight" 
                            class="form__field form__field--number" 
                            :class="{'imperial-height' : heightUnit === 'ft'}"
                            :placeholder="placeholders.height" 
                            :value="heightMain"
                            @change="heightChange">

                    <span class="account__height-separator" v-if="heightUnit === 'ft'">'</span>
                    <input  :value="heightInches" 
                            type="number" 
                            :min="minHeightInches" 
                            :max="maxHeightInches" 
                            v-if="heightUnit === 'ft'" 
                            class="form__field form__field--number imperial-height" 
                            :placeholder="placeholders.inch_abbr"
                            @change="heightChange($event, true)"
                            />
                    <span class="account__height-separator" v-if="heightUnit === 'ft'">"</span>

                    <div class="account__unit">{{heightUnit}}</div>
                </div>
                <div class="form__item form__item--half">
                    <input  type="number" 
                            :min="minWeight" 
                            :max="maxWeight"
                            @change="weightChange"
                            class="form__field form__field--number" 
                            :placeholder="placeholders.weight" 
                            :value="weightMain">
                    
                    <div class="account__unit">{{weightUnit}}</div>
                </div>
            </div>

            <!-- change password -->
            <template v-if="!user.socialLogin">
                <div class="form__title h5" v-translate>change_password</div>
                <div class="form__row form__row--multiple">
                    <div class="form__item form__item--half">
                        <input 
                            type="password" 
                            class="form__field form__field--text" 
                            :placeholder="placeholders.current_password" 
                            @input="passwordFieldChange"
                            v-model="passwords.currentPassword"
                            :class="{'is-error': passwords.errors.indexOf('current-password') >= 0 || passwords.errors.indexOf('current-password-wrong') >= 0}"
                            />
                        <div class="form__error" v-if="passwords.errors.indexOf('current-password') >= 0">"{{placeholders.current_password}}" <translate>form_field_required</translate></div>
                        <div class="form__error" v-if="passwords.errors.indexOf('current-password-wrong') >= 0" v-translate>wrong_password</div>
                    </div>
                    <div class="form__item form__item--half">
                    </div>
                </div>
                <div class="form__row form__row--multiple form__row--space-below">
                    <div class="form__item form__item--half">
                        <input 
                            type="password" 
                            class="form__field form__field--text" 
                            :placeholder="placeholders.new_password" 
                            @input="passwordFieldChange" 
                            v-model="passwords.newPassword"
                            :class="{'is-error': passwords.errors.indexOf('new-passwords-different') >= 0 || passwords.errors.indexOf('new-password-tooshort') >= 0}"
                            />
                            <div class="form__error" v-if="passwords.errors.indexOf('new-password-tooshort') >= 0" v-translate>password_min_characters</div>
                    </div>
                    <div class="form__item form__item--half">
                        <input 
                            type="password" 
                            class="form__field form__field--text" 
                            :placeholder="placeholders.confirm_password" 
                            @input="passwordFieldChange"
                            v-model="passwords.confirmPassword"
                            :class="{'is-error': passwords.errors.indexOf('new-passwords-different') >= 0}"
                            />
                        <div class="form__error" v-if="passwords.errors.indexOf('new-passwords-different') >= 0" v-translate>passwords_dont_match</div>
                    </div>
                </div>
            </template>

            <!-- privacy -->
            <div class="form__title h5" v-translate>privacy</div>
            <div class="form__row form__row--multiple">
                <div class="form__item form__item--half">
                    <div class="form__field form__field--radio">
                        <input type="radio" id="profile-private" name="profile-privacy" v-model="formData.private" :value="true">
                        <label for="profile-private">
                            <span class="h6" v-translate>private</span>
                            <span class="text text--small" v-translate>only_me</span>
                        </label>
                    </div>
                </div>
                <div class="form__item form__item--half">
                    <div class="form__field form__field--radio">
                        <input type="radio" id="profile-public" name="profile-privacy" v-model="formData.private" :value="false">
                        <label for="profile-public">
                            <span class="h6" v-translate>public</span>
                            <span class="text text--small" v-translate>everyone</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- units -->
            <div class="form__title h5" v-translate>units</div>
            <div class="form__row form__row--multiple form__row--space-below">
                <div class="form__item form__item--half">
                    <div class="form__field form__field--radio">
                        <input type="radio" id="unit-metric" name="profile-units" v-model="formData.unit" value="metric">
                        <label for="unit-metric">
                            <span class="h6" v-translate>metric</span>
                        </label>
                    </div>
                </div>
                <div class="form__item form__item--half">
                    <div class="form__field form__field--radio">
                        <input type="radio" id="unit-imperial" name="profile-units" v-model="formData.unit" value="imperial">
                        <label for="unit-imperial">
                            <span class="h6" v-translate>imperial</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- pumatrac offers -->
            <div class="form__title account__offers__title  h5" v-translate>pumatrac_offers</div>
            <div class="account__offers__description text" v-translate>pumatrac_offers_description</div>
            <div class="form__row form__row--multiple form__row--space-below">
                <div class="form__item form__item--half">
                    <div class="form__field form__field--radio">
                        <input type="radio" id="offer-on" name="profile-offer" v-model="offerRetargeting" value="true"  @change="consent('retargeting', 'accept')">
                        <label for="offer-on">
                            <span class="h6" v-translate>on</span>
                        </label>
                    </div>
                </div>
                <div class="form__item form__item--half">
                    <div class="form__field form__field--radio">
                        <input type="radio" id="offer-off" name="profile-offer" v-model="offerRetargeting" value="false" @change="consent('retargeting', 'reject')">
                        <label for="offer-off">
                            <span class="h6" v-translate>off</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- email retargeting -->
            <div class="form__title account__offers__title  h5" v-translate>email_retargeting_title</div>
            <div class="account__offers__description text" v-translate>email_retargeting_description</div>
            <div class="form__row form__row--multiple form__row--space-below">
                <div class="form__item form__item--half">
                    <div class="form__field form__field--radio">
                        <input type="radio" id="email-on" name="profile-email-retargeting" v-model="emailRetargeting" value="true"  @change="consent('email', 'accept')">
                        <label for="email-on">
                            <span class="h6" v-translate>on</span>
                        </label>
                    </div>
                </div>
                <div class="form__item form__item--half">
                    <div class="form__field form__field--radio">
                        <input type="radio" id="email-off" name="profile-email-retargeting" v-model="emailRetargeting" value="false"  @change="consent('email', 'reject')">
                        <label for="email-off">
                            <span class="h6" v-translate>off</span>
                        </label>
                    </div>
                </div>
            </div>

            <!-- button -->
            <div class="form__row">
                <div class="form__item">
                    <puma-button @click="submitProfileUpdate" :loading="submitButtonLoading" :disabled="!isFormValid"><translate>save_preferences</translate></puma-button>
                    <div class="form__error" v-if="submitErrors.length > 0">
                        <div v-for="error in submitErrors" :key="error">{{error}}</div>
                    </div>
                    <div class="form__error form__error--success" v-if="submitSuccess.length > 0">
                        <div v-for="success in submitSuccess" :key="success">{{success}}</div>
                    </div> 
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import * as api from '../api'
import _ from 'lodash'
import { presign, postToS3 } from '../api'
import { mapGetters } from "vuex"
import Datepicker from 'vuejs-datepicker';
import { storeUser } from '../utils/authentication'
import countries from '../data/countries';

import {addTrackingObject} from "@/utils/tracking"

export default {
    name: 'Account',
    components: {
        Datepicker
    },
    data() {
        return {
            errors: [],
            emailRetargeting: false,
            offerRetargeting: false,
            formData: {
                profileImageKey: '',
                name: '',
                email: '',
                dob: '',
                location: '',
                sex: '',
                height: 0,
                weight: 0,
                private: false,
                unit: 'metric',
                settings: {},
            },
            imagePreviewUrl: false,
            imageFile: false,
            imageUploading: false,
            submitButtonLoading: false,
            minHeightInches: 0,
            maxHeightInches: 11,
            initialDate: new Date(1990, 1, 1),
            disabledDates: {
                to: new Date(1900, 0, 0),
                from: new Date(2016, 0, 0)
            },
            passwords: {
                errors: [],
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            },
            submitErrors: [],
            submitSuccess: [],
        }
    },
    computed: {
         ...mapGetters(['user']),
        placeholders() {
            return {
                name: this.$gettext('first_and_last_name'),
                email: this.$gettext('email'),
                location: this.$gettext('location'),
                birthDate: this.$gettext('birth_date'),
                gender: this.$gettext('gender'),
                weight: this.$gettext('weight'),
                height: this.$gettext('height'),
                feet_abbr: this.$gettext('feet_abbr'),
                inch_abbr: this.$gettext('inch_abbr'),
                current_password: 'Current password',
                new_password: 'New password',
                confirm_password: 'Confirm new password',
            }
        },
        genderOptions() {
            return [
                {
                    label: this.$gettext('male'),
                    code: 'm'
                },
                {
                    label: this.$gettext('female'),
                    code: 'f'
                },
                {
                    label: this.$gettext('non_binary'),
                    code: 'd'
                },
            ]
        },
        countryOptions() {
            return [
                _.map(countries, (countryName, countryKey) => {
                    return {
                        code: countryKey,
                        label: countryName,
                    }
                })
            ];
        },
        minHeight() {
            return this.formData.unit === 'metric' ? 120 : 4
        },
        maxHeight() {
            return this.formData.unit === 'metric' ? 219 : 7
        },
        minWeight() {
            return this.formData.unit === 'metric' ? 30 : 66
        },
        maxWeight() {
            return this.formData.unit === 'metric' ? 199 : 439
        },
        weightUnit() {
            return this.formData.unit === 'metric' ? 'kg' : 'lbs'
        },
        heightUnit() {
            return this.formData.unit === 'metric' ? 'cm' : 'ft'
        },
        heightMain() {
            // cm or feet
            let height = this.formData.height
            if(this.formData.unit === 'imperial') {
                height = Math.floor(height * 0.0328084)
            }
            return height;
        },
        heightInches() {
            let height = this.formData.height
            height = height * 0.393701 // cm to inches
            height = height - (this.heightMain * 12) // substract heightmain (feet)
            return Math.floor(height)
        },
        weightMain() {
            // kg or lbs
            let weight = this.formData.weight
            if(this.formData.unit === 'imperial') {
                weight = Math.round(weight * 2.20462)
            }
            return weight;
        },
        willChangePassword() {
            return this.passwords.newPassword !== '' || this.passwords.confirmPassword !== ''
        },
        isFormValid() {
            let isValid = this.errors.length === 0
            if(this.willChangePassword) isValid = this.errors.length === 0 && this.passwords.errors.length === 0
            return isValid && !this.imageUploading
        }
    },
    mounted() {
        this.prepareFormData()
        this.loadConsent()
        addTrackingObject({subSection: "profile", screenName: "settingsProfile"}, {type: "profile", value: "web_userprofile_account"});
    },
    methods: {
        prepareFormData () {
            this.formData.name = this.user.name
            this.formData.email = this.user.email
            this.formData.dob = this.user.dob
            this.formData.location = this.user.location
            this.formData.sex = this.user.sex
            this.formData.height = this.user.height
            this.formData.weight = this.user.weight
            this.formData.private = this.user.private
            this.formData.unit = this.user.settings.unitsDistance === 'm' ? 'metric' : 'imperial'
            this.formData.settings = this.user.settings
        },
        requiredFieldChange() {
            this.errors = []

            if(this.formData.name === '') {
                this.errors.push('name')
            }
            if(this.formData.email === '') {
                this.errors.push('email')
            }
            if(this.formData.location === '') {
                this.errors.push('location')
            }
            // if(!this.formData.sex || this.formData.sex === '') {
            //     this.errors.push('sex')
            // }
        },
        onImageFileChange(e) {
            const file = e.target.files[0]
            this.imageFile = file
            this.imagePreviewUrl = URL.createObjectURL(file)
            this.uploadImage(file);
        },
        async uploadImage(file) {
            this.imageUploading = true;
            try {
                let presignResponse = await presign()
                const presignResponseData = presignResponse.data;
                let imageKey = presignResponseData.fields.key;
                let s3Response = await postToS3(presignResponseData, file);
                this.formData.profileImageKey = imageKey
            } catch(e) {
                console.error(e);
            } finally {
                this.imageUploading = false;
            }
        },
        handleDateChange(date) {
            if(!date || date === '') {
                return null;
            }
            console.log(date)
            this.formData.dob = date.toISOString().split('T')[0]
            this.requiredFieldChange();
        },
        submitProfileUpdate() {
            this.submitErrors = []
            this.submitSuccess = []
            
            if(this.isFormValid) {
                this.updateProfile()
                if(this.willChangePassword) {
                    this.updatePassword()
                }
            }
        },
        async updatePassword() {
            let data = {
                user: {
                    currentPassword: this.passwords.currentPassword,
                    newPassword: this.passwords.newPassword
                }
            }

            this.submitButtonLoading = true
            try {
                const response = await api.users.changePassword(data)
                const responseData = response.data.data
                storeUser({authToken: responseData.authToken});
                this.passwords = {
                    errors: [],
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                }
                this.submitSuccess.push('Password has been successfully changed.')
            } catch(error) {
                console.error(error)
                if(error.response.status === 403) {
                    this.passwords.errors.push('current-password-wrong')
                }
            } finally {
                this.submitButtonLoading = false;
            }
        },
        async consent(screen, status) {
            console.log(screen, status);
            try {
                const response = await api.users.consent(screen, status)
            } catch(error) {
                console.log(error);
            }
        },

        async loadConsent() {
            try {
                const response = await api.users.loadConsent();
                this.emailRetargeting = response.data.data[1].given
                this.offerRetargeting = response.data.data[0].given
                // console.log(this.emailRetargeting, this.offerRetargeting);
                // console.log(response.data.data[0].type,'-', response.data.data[0].given);
                // console.log(response.data.data[1].type,'-', response.data.data[1].given);
            } catch(error) {
                console.log(error);
            }
        },
        async updateProfile() {
            let data = {
                user: {...this.formData}
            }

            if(data.user.profileImageKey !== undefined && data.user.profileImageKey === '') {
                delete data.user['profileImageKey'];
            }

            data.user.settings.unitsDistance  = this.formData.unit === 'metric' ? 'm' : 'i'
            data.user.settings.unitsTemperature = this.formData.unit === 'metric' ? 'c' : 'f'

            this.submitButtonLoading = true;
            try {
                const response = await api.users.updateProfile(data);
                const responseData = response.data.data;
                storeUser(responseData);

                this.submitSuccess.push('Profile has been successfully updated.')
            } catch (error) {
                console.error(error);
                console.log(error.response);
                if(error.response.status === 422) {
                    this.submitErrors.push(this.$gettext('form_error_cant_update_user'))
                }
            } finally {
                this.submitButtonLoading = false;
            }
        },
        heightChange(e, isInches = false) {
            let value = parseInt(e.target.value);
            if(this.formData.unit === 'metric') {
                this.formData.height = Math.round(value * 100) / 100
            } else {
                let heightCm = 0
                if(isInches) {
                    heightCm += this.heightMain * 30.48 // feet
                    heightCm += value * 2.54 // inches
                } else {
                    heightCm += value * 30.48 // feet
                    heightCm += this.heightInches * 2.54 // inches
                }
                this.formData.height = Math.round(heightCm * 100) / 100
            }
        },
        weightChange(e) {
            let value = parseInt(e.target.value)
            if(this.formData.unit === 'metric') {
                this.formData.weight = Math.round(value * 100) / 100
            } else {
                this.formData.weight = Math.round((value * 0.453592) * 100) / 100
            }
        },
        passwordFieldChange() {
            this.passwords.errors = []

            if(this.passwords.newPassword !== '' || this.passwords.confirmPassword !== '') {
                if(this.passwords.newPassword !== this.passwords.confirmPassword) {
                    this.passwords.errors.push('new-passwords-different')
                }
                if(this.passwords.newPassword.length < 6) {
                    this.passwords.errors.push('new-password-tooshort')
                }
                if(this.passwords.currentPassword.length === 0) {
                    this.passwords.errors.push('current-password')
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.account {
    $block: &;
    max-width: 700px;
    padding-bottom: 80px;

    @include mq($until: tablet-landscape) {
        padding-bottom: 60px;
    }

    &__title {
        margin-bottom: 10px;
    }

    &__text {
        margin-bottom: 50px;
    }

    &__form {
        max-width: 600px;
    }

    &__profile-image {
        position: relative;
        display: flex;
        align-items: center;
        margin-bottom: 50px;

        @include mq($until: tablet) {
            margin-bottom: 30px;
        }

        &__image {
            position: relative;
            width: 70px;
            height: 70px;
            border-radius: 100%;
            overflow: hidden;
            background: black;

            img {
                border-radius: 100%;
                @include object-fit;
            }
        }

        &__loader {
            @include fillout;
            border-radius: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(black, 0.5);
        }

        &__label {
            margin-left: 40px;
            border-bottom: 1px solid black;

            @include mq($until: tablet) {
                margin-left: 20px;
            }
        }

        &__input {
            @include fillout;
            opacity: 0;
            cursor: pointer;
        }
    }

    &__unit {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 20px;
        opacity: 0.5;
        pointer-events: none;
    }

    .imperial-height {
        width: 3.5em;
        padding: 0.5em 1em;
    }

    &__height-separator {
        display: inline-block;
        margin: 0 8px 0 4px;
    }

    &__offers {

        &__title {
            margin-bottom: 10px;
        }

        &__description {
            padding-bottom: 22px;
        }
    }
}
</style>